import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Lock,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Upload,
  Car,
  Loader2,
  CheckCircle2,
  AlertCircle,
  FileText,
  Mail,
  Phone,
  ChevronDown,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const withCreds = { withCredentials: true };

const STATUT_BADGE = {
  Disponible: "bg-emerald-100 text-emerald-700",
  Réservé: "bg-amber-100 text-amber-700",
  Vendu: "bg-slate-200 text-slate-600",
};

const emptyForm = {
  marque: "",
  modele: "",
  version: "",
  annee: new Date().getFullYear(),
  kilometrage: 0,
  energie: "Essence",
  boite: "Manuelle",
  prix: 0,
  description: "",
  photos: [],
  statut: "Disponible",
};

const ENERGIES = ["Essence", "Diesel", "Hybride", "Électrique", "GPL"];
const BOITES = ["Manuelle", "Automatique"];
const STATUTS = ["Disponible", "Réservé", "Vendu"];

function formatPrice(n) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

function Login({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password }, withCreds);
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Mot de passe incorrect";
      setError(typeof detail === "string" ? detail : "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 grid place-items-center p-4">
      <form
        onSubmit={onSubmit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-8 shadow-2xl"
      >
        <div className="h-12 w-12 grid place-items-center rounded-md bg-blue-600/10 border border-blue-500/30 text-blue-400 mb-5">
          <Lock size={22} />
        </div>
        <h1 className="font-display font-black text-3xl text-white tracking-tight mb-2">
          Espace admin
        </h1>
        <p className="text-slate-400 text-sm mb-7">
          Garage Deschamps Auto — gestion des véhicules d'occasion
        </p>

        <label className="block text-sm font-semibold text-slate-200 mb-1.5">
          Mot de passe
        </label>
        <input
          type="password"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          required
          className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
        />

        {error && (
          <div
            data-testid="admin-login-error"
            className="mt-4 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300"
          >
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !password}
          data-testid="admin-login-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 px-5 py-3 text-sm font-semibold text-white transition-colors"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Connexion…
            </>
          ) : (
            "Se connecter"
          )}
        </button>

        <a
          href="/"
          className="mt-4 block text-center text-xs text-slate-500 hover:text-slate-300"
        >
          ← Retour au site
        </a>
      </form>
    </div>
  );
}

function VehicleForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || emptyForm);
  const [uploadErr, setUploadErr] = useState("");

  const onChange = (k) => (e) => {
    const v = e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onNumChange = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: Number(e.target.value) || 0 }));
  };

  const onFiles = async (e) => {
    setUploadErr("");
    const files = Array.from(e.target.files || []);
    const maxSize = 3 * 1024 * 1024; // 3 MB per file
    const added = [];
    for (const f of files) {
      if (f.size > maxSize) {
        setUploadErr(`"${f.name}" dépasse 3 Mo. Compressez-la avant.`);
        continue;
      }
      try {
        const b64 = await fileToCompressedDataURL(f, 1400, 0.8);
        added.push(b64);
      } catch {
        setUploadErr("Impossible de lire une image");
      }
    }
    setForm((f) => ({ ...f, photos: [...(f.photos || []), ...added] }));
    e.target.value = "";
  };

  const removePhoto = (idx) => {
    setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));
  };

  const movePhoto = (idx, dir) => {
    setForm((f) => {
      const arr = [...f.photos];
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return f;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      return { ...f, photos: arr };
    });
  };

  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form
      onSubmit={submit}
      data-testid="admin-vehicle-form"
      className="space-y-5"
    >
      <div className="grid md:grid-cols-3 gap-4">
        <Input label="Marque *" value={form.marque} onChange={onChange("marque")} required testid="admin-field-marque" />
        <Input label="Modèle *" value={form.modele} onChange={onChange("modele")} required testid="admin-field-modele" />
        <Input label="Version (optionnel)" value={form.version} onChange={onChange("version")} placeholder="1.6 HDi 110ch" testid="admin-field-version" />
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        <Input label="Année *" type="number" value={form.annee} onChange={onNumChange("annee")} required testid="admin-field-annee" />
        <Input label="Kilométrage *" type="number" value={form.kilometrage} onChange={onNumChange("kilometrage")} required testid="admin-field-km" />
        <Select label="Énergie *" value={form.energie} onChange={onChange("energie")} options={ENERGIES} testid="admin-field-energie" />
        <Select label="Boîte *" value={form.boite} onChange={onChange("boite")} options={BOITES} testid="admin-field-boite" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Input label="Prix (€) *" type="number" value={form.prix} onChange={onNumChange("prix")} required testid="admin-field-prix" />
        <Select label="Statut" value={form.statut} onChange={onChange("statut")} options={STATUTS} testid="admin-field-statut" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1.5">
          Description
        </label>
        <textarea
          data-testid="admin-field-description"
          value={form.description}
          onChange={onChange("description")}
          rows={4}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 resize-none"
          placeholder="Historique, équipements, points forts…"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-2">
          Photos (la 1ère sert de vignette)
        </label>
        <label className="flex items-center justify-center gap-2 cursor-pointer rounded-md border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 px-4 py-6 text-sm text-slate-600 transition-colors">
          <Upload size={16} />
          <span>Cliquez pour ajouter une ou plusieurs photos (JPG/PNG, max 3 Mo)</span>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={onFiles}
            className="hidden"
            data-testid="admin-field-photos"
          />
        </label>
        {uploadErr && (
          <div className="mt-2 text-xs text-red-600">{uploadErr}</div>
        )}
        {form.photos?.length > 0 && (
          <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-3">
            {form.photos.map((src, i) => (
              <div key={src} className="relative group aspect-square rounded-md overflow-hidden border border-slate-200 bg-slate-50">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                  <button type="button" onClick={() => removePhoto(i)} className="text-white text-xs hover:text-red-300 flex items-center gap-1"><Trash2 size={12}/>Supprimer</button>
                  <div className="flex gap-1">
                    <button type="button" disabled={i===0} onClick={() => movePhoto(i, -1)} className="text-white/80 disabled:opacity-30 text-xs px-1.5 py-0.5 rounded bg-white/10">←</button>
                    <button type="button" disabled={i===form.photos.length-1} onClick={() => movePhoto(i, 1)} className="text-white/80 disabled:opacity-30 text-xs px-1.5 py-0.5 rounded bg-white/10">→</button>
                  </div>
                </div>
                {i === 0 && (
                  <div className="absolute top-1 left-1 bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                    Principale
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2.5 text-sm font-semibold text-slate-700 hover:text-slate-900"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={saving}
          data-testid="admin-form-submit"
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 px-5 py-2.5 text-sm font-semibold text-white"
        >
          {saving ? <><Loader2 size={14} className="animate-spin"/>Enregistrement…</> : <><CheckCircle2 size={14}/>Enregistrer</>}
        </button>
      </div>
    </form>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-800 mb-1.5">
        {label}
      </label>
      <input
        {...props}
        data-testid={testid}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      />
    </div>
  );
}

function Select({ label, options, testid, ...props }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-800 mb-1.5">
        {label}
      </label>
      <select
        {...props}
        data-testid={testid}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

function fileToCompressedDataURL(file, maxDim = 1400, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function QuotesTab({ onLogout, showToast }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, withCreds);
      setQuotes(data || []);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (q) => {
    if (!window.confirm(`Supprimer la demande de ${q.prenom} ${q.nom} ?`)) return;
    try {
      await axios.delete(`${API}/quotes/${q.id}`, withCreds);
      showToast("Demande supprimée");
      await load();
    } catch (err) {
      alert("Erreur : " + (err?.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return <div className="text-center text-slate-500 py-20">Chargement…</div>;
  }

  if (quotes.length === 0) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center" data-testid="admin-quotes-empty">
        <FileText size={48} className="mx-auto text-slate-300 mb-4" strokeWidth={1.5} />
        <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Aucune demande de devis</h3>
        <p className="text-slate-600 text-sm">Les demandes envoyées depuis le site apparaîtront ici.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-quotes-list">
      {quotes.map((q) => {
        const open = openId === q.id;
        return (
          <div key={q.id} data-testid={`admin-quote-${q.id}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenId(open ? null : q.id)}
              data-testid={`admin-quote-toggle-${q.id}`}
              className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-slate-50"
            >
              <div className="h-10 w-10 shrink-0 grid place-items-center rounded-md bg-blue-600/10 text-blue-600">
                <FileText size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-semibold text-slate-900">{q.prenom} {q.nom}</span>
                  <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                    {q.service_type}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {q.vehicule} — {formatDate(q.created_at)}
                </div>
              </div>
              <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
              <div className="px-5 pb-5 pt-1 border-t border-slate-100">
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm mt-4">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Mail size={14} className="text-slate-400 shrink-0" />
                    <a href={`mailto:${q.email}`} className="text-blue-600 hover:underline break-all" data-testid={`admin-quote-email-${q.id}`}>{q.email}</a>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <Phone size={14} className="text-slate-400 shrink-0" />
                    <a href={`tel:${q.telephone}`} className="text-blue-600 hover:underline">{q.telephone}</a>
                  </div>
                  <div className="text-slate-700">
                    <span className="text-slate-500">Véhicule :</span> {q.vehicule}
                  </div>
                  <div className="text-slate-700">
                    <span className="text-slate-500">Immatriculation :</span>{" "}
                    <span className="font-mono font-semibold uppercase">{q.immatriculation}</span>
                  </div>
                </div>
                <div className="mt-4 rounded-md bg-slate-50 border border-slate-100 p-4 text-sm text-slate-700 whitespace-pre-wrap">
                  {q.description}
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => remove(q)}
                    data-testid={`admin-quote-delete-${q.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md text-red-600 hover:bg-red-50 px-3 py-2 text-sm font-semibold"
                  >
                    <Trash2 size={14} /> Supprimer
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function VehicleRow({ v, onEdit, onRemove }) {
  return (
    <tr data-testid={`admin-row-${v.id}`} className="hover:bg-slate-50">
      <td className="px-5 py-3">
        {v.photos?.[0] ? (
          <img src={v.photos[0]} alt="" className="h-12 w-16 object-cover rounded-md"/>
        ) : (
          <div className="h-12 w-16 rounded-md bg-slate-100 grid place-items-center text-slate-300">
            <Car size={16}/>
          </div>
        )}
      </td>
      <td className="px-5 py-3">
        <div className="font-semibold text-slate-900">{v.marque} {v.modele}</div>
        {v.version && <div className="text-xs text-slate-500">{v.version}</div>}
      </td>
      <td className="px-5 py-3 hidden md:table-cell text-slate-700">{v.annee}</td>
      <td className="px-5 py-3 hidden md:table-cell text-slate-700">
        {new Intl.NumberFormat("fr-FR").format(v.kilometrage)}
      </td>
      <td className="px-5 py-3 font-semibold text-blue-600">{formatPrice(v.prix)}</td>
      <td className="px-5 py-3">
        <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUT_BADGE[v.statut] || STATUT_BADGE.Vendu}`}>
          {v.statut}
        </span>
      </td>
      <td className="px-5 py-3 text-right">
        <div className="inline-flex items-center gap-1">
          <button
            onClick={() => onEdit(v)}
            data-testid={`admin-edit-${v.id}`}
            className="h-8 w-8 grid place-items-center rounded-md text-slate-600 hover:bg-slate-100"
          >
            <Pencil size={14}/>
          </button>
          <button
            onClick={() => onRemove(v)}
            data-testid={`admin-delete-${v.id}`}
            className="h-8 w-8 grid place-items-center rounded-md text-red-600 hover:bg-red-50"
          >
            <Trash2 size={14}/>
          </button>
        </div>
      </td>
    </tr>
  );
}

function VehiclesTab({ vehicles, loading, onAdd, onEdit, onRemove }) {
  let body;
  if (loading) {
    body = <div className="text-center text-slate-500 py-20">Chargement…</div>;
  } else if (vehicles.length === 0) {
    body = (
      <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center">
        <Car size={48} className="mx-auto text-slate-300 mb-4" strokeWidth={1.5}/>
        <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Aucun véhicule enregistré</h3>
        <p className="text-slate-600 text-sm">Cliquez sur "Ajouter un véhicule" pour commencer.</p>
      </div>
    );
  } else {
    body = (
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-5 py-3">Photo</th>
              <th className="text-left px-5 py-3">Véhicule</th>
              <th className="text-left px-5 py-3 hidden md:table-cell">Année</th>
              <th className="text-left px-5 py-3 hidden md:table-cell">Km</th>
              <th className="text-left px-5 py-3">Prix</th>
              <th className="text-left px-5 py-3">Statut</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {vehicles.map((v) => (
              <VehicleRow key={v.id} v={v} onEdit={onEdit} onRemove={onRemove} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 tracking-tight">
            Véhicules
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            {vehicles.length} véhicule{vehicles.length > 1 ? "s" : ""} —
            {" "}{vehicles.filter(v=>v.statut==="Disponible").length} disponible(s),
            {" "}{vehicles.filter(v=>v.statut==="Vendu").length} vendu(s)
          </p>
        </div>
        <button
          onClick={onAdd}
          data-testid="admin-add-vehicle"
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm lift"
        >
          <Plus size={16}/> Ajouter un véhicule
        </button>
      </div>
      {body}
    </>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | vehicle obj
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/vehicles`, withCreds);
      setVehicles(data || []);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2800);
  };

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.patch(`${API}/admin/vehicles/${editing.id}`, form, withCreds);
        showToast("Véhicule mis à jour");
      } else {
        await axios.post(`${API}/admin/vehicles`, form, withCreds);
        showToast("Véhicule ajouté");
      }
      setEditing(null);
      await load();
    } catch (err) {
      alert("Erreur : " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Supprimer ${v.marque} ${v.modele} ?`)) return;
    try {
      await axios.delete(`${API}/admin/vehicles/${v.id}`, withCreds);
      showToast("Véhicule supprimé");
      await load();
    } catch (err) {
      alert("Erreur : " + (err?.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 grid place-items-center rounded-md bg-blue-600 text-white">
              <Car size={18} />
            </span>
            <div>
              <div className="text-xs text-slate-400 uppercase tracking-wider">Admin</div>
              <div className="font-display font-black text-sm">Deschamps Auto</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-slate-300 hover:text-white">Voir le site ↗</a>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"
            >
              <LogOut size={14}/> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-2 mb-8 border-b border-slate-200">
          <button
            onClick={() => setTab("vehicles")}
            data-testid="admin-tab-vehicles"
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === "vehicles"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Car size={15} /> Véhicules
          </button>
          <button
            onClick={() => setTab("quotes")}
            data-testid="admin-tab-quotes"
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === "quotes"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <FileText size={15} /> Demandes de devis
          </button>
        </div>

        {tab === "quotes" ? (
          <>
            <div className="mb-8">
              <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 tracking-tight">
                Demandes de devis
              </h1>
              <p className="text-slate-600 mt-1 text-sm">
                Demandes reçues depuis le formulaire du site (également envoyées par email).
              </p>
            </div>
            <QuotesTab onLogout={onLogout} showToast={showToast} />
          </>
        ) : (
          <VehiclesTab
            vehicles={vehicles}
            loading={loading}
            onAdd={() => setEditing({})}
            onEdit={setEditing}
            onRemove={remove}
          />
        )}
      </main>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm overflow-y-auto p-4 md:p-8">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-slate-200">
              <h2 className="font-display font-black text-xl text-slate-900">
                {editing.id ? `Modifier — ${editing.marque} ${editing.modele}` : "Nouveau véhicule"}
              </h2>
              <button onClick={() => setEditing(null)} className="h-9 w-9 grid place-items-center rounded-md hover:bg-slate-100 text-slate-600">
                <X size={18}/>
              </button>
            </div>
            <div className="p-6 md:p-8">
              <VehicleForm
                initial={editing.id ? editing : emptyForm}
                onSave={save}
                onCancel={() => setEditing(null)}
                saving={saving}
              />
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          data-testid="admin-toast"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-5 py-3 rounded-md shadow-2xl flex items-center gap-2 text-sm z-[200]"
        >
          <CheckCircle2 size={16} className="text-emerald-400"/> {toast}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/me`, withCreds)
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, withCreds);
    } catch {
      // cookie cleared server-side; ignore network errors
    }
    setAuthed(false);
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen bg-slate-950 grid place-items-center">
        <Loader2 size={24} className="text-white animate-spin"/>
      </div>
    );
  }

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;
  return <Dashboard onLogout={logout} />;
}
